import type { FC, ReactNode } from 'react'
import type { MessageContext } from '@/lib/whatsapp'

import { useCallback, useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { buildWhatsAppUrl } from '@/lib/whatsapp'
import { useSettings } from '@/components/providers/settings-provider'
import { WhatsAppIcon } from '@/components/shop/shop-bits'

// An order the link records in the dashboard when it is clicked. Prices are looked up on the server.
export interface WhatsAppOrder {
    source: 'keranjang' | 'produk'
    items: Array<{ productId: string, quantity: number, colorLabel: string | null }>
    customerName?: string
    note?: string
}

interface WhatsAppLinkProps {
    // Builds the message. Called with an empty origin and order code for the first render (server and
    // hydration), then again after mount with the real site origin and a fresh order code.
    message: (context: MessageContext) => string
    children: ReactNode
    className?: string
    variant?: 'solid' | 'outline' | 'ghost'
    size?: 'md' | 'lg' | 'sm'
    order?: WhatsAppOrder
    disabled?: boolean
    onClick?: () => void
    onOrdered?: (orderCode: string) => void
}

const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'

const createOrderCode = (): string => {
    const bytes = new Uint8Array(6)

    window.crypto.getRandomValues(bytes)

    return `EMA-${Array.from(bytes, byte => CODE_ALPHABET[byte % CODE_ALPHABET.length]).join('')}`
}

// A real link to WhatsApp with a pre-filled message. The href is refreshed after mount so it can
// include the full page address and an order code without a server/client mismatch.
export const WhatsAppLink: FC<WhatsAppLinkProps> = ({
    message,
    children,
    className,
    variant = 'solid',
    size = 'md',
    order,
    disabled = false,
    onClick,
    onOrdered
}): ReactNode => {
    const settings = useSettings()
    const [origin, setOrigin] = useState('')
    const [orderCode, setOrderCode] = useState('')
    const renew = useRef<ReturnType<typeof setTimeout> | null>(null)
    const wantsCode = Boolean(order)

    useEffect(() => {
        setOrigin(window.location.origin)

        if (wantsCode) setOrderCode(createOrderCode())

        return () => {
            if (renew.current) clearTimeout(renew.current)
        }
    }, [wantsCode])

    const href = buildWhatsAppUrl(message({ origin, storeName: settings.storeName, orderCode }), settings.whatsappNumber)

    const handleClick = useCallback(() => {
        onClick?.()

        if (!order || !orderCode || !order.items.length) return

        const code = orderCode

        try {
            fetch('/api/orders', {
                method: 'POST',
                keepalive: true,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...order, code })
            }).catch(() => undefined)
        }
        catch {
            // Recording the order is a bonus; WhatsApp still opens with the full message.
        }

        // Let the browser follow the link before the parent swaps this button out.
        if (onOrdered) window.setTimeout(() => onOrdered(code), 200)

        // A fresh code for the next order, while a quick double click keeps the same one.
        if (renew.current) clearTimeout(renew.current)

        renew.current = setTimeout(() => setOrderCode(createOrderCode()), 2500)
    }, [onClick, order, orderCode, onOrdered])

    const classes = cn(
        'inline-flex items-center justify-center gap-2 rounded-full font-semibold transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-offset-2',
        size === 'lg' && 'h-14 px-7 text-base',
        size === 'md' && 'h-12 px-6 text-sm',
        size === 'sm' && 'h-10 px-4 text-sm',
        variant === 'solid' && 'bg-sage-700 text-cream-50 shadow-[0_10px_30px_-12px_rgba(59,79,55,0.7)] hover:-translate-y-0.5 hover:bg-sage-600',
        variant === 'outline' && 'border border-sage-600/40 bg-white/70 text-sage-700 hover:border-sage-600 hover:bg-sage-50',
        variant === 'ghost' && 'text-sage-700 hover:bg-sage-50',
        disabled && 'pointer-events-none opacity-50',
        className
    )

    if (disabled) {
        return (
            <span aria-disabled='true' className={classes}>
                <WhatsAppIcon className={size === 'sm' ? 'size-4' : 'size-5'} />
                {children}
            </span>
        )
    }

    return (
        <a
            href={href}
            target='_blank'
            rel='noopener noreferrer'
            onClick={handleClick}
            className={classes}
        >
            <WhatsAppIcon className={size === 'sm' ? 'size-4' : 'size-5'} />
            {children}
        </a>
    )
}
